// frontend/src/pages/Projects.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import {
    PlusIcon,
    BriefcaseIcon,
    ArrowRightIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';

const Projects = ({ embedded = false }) => {
    const [loading, setLoading] = useState(true);
    const [projects, setProjects] = useState([]);
    const [stats, setStats] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [projectsRes, statsRes] = await Promise.all([
                axios.get('/projects/my-projects'),
                axios.get('/projects/statistics')
            ]);
            setProjects(projectsRes.data.data);
            setStats(statsRes.data.data);
        } catch (error) {
            toast.error('Failed to load projects');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-4" role="status" aria-live="polite">
                <div className="skeleton h-10 w-56" />
                <div className="skeleton h-40" />
                <div className="skeleton h-24" />
                <span className="sr-only">Loading projects…</span>
            </div>
        );
    }

    return (
        <div className="w-full space-y-6">
            {embedded ? (
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-ink-600">Manage your individual and team projects.</p>
                    <button type="button" onClick={() => setShowCreateForm(!showCreateForm)} className="btn-primary">
                        <PlusIcon className="w-5 h-5" aria-hidden="true" />
                        New Project
                    </button>
                </div>
            ) : (
                <PageHeader
                    title="Internship Projects"
                    description="Manage your individual and team projects"
                    actions={
                        <button type="button" onClick={() => setShowCreateForm(!showCreateForm)} className="btn-primary">
                            <PlusIcon className="w-5 h-5" aria-hidden="true" />
                            New Project
                        </button>
                    }
                />
            )}

            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="kpi-card text-center animate-fade-up stagger-1">
                        <p className="text-xs font-semibold uppercase tracking-wider text-ink-500">Total</p>
                        <p className="mt-1 text-xl font-bold text-ink-950 tabular-nums">{stats.total || 0}</p>
                    </div>
                    <div className="kpi-card text-center animate-fade-up stagger-2">
                        <p className="text-xs font-semibold uppercase tracking-wider text-ink-500">Planning</p>
                        <p className="mt-1 text-xl font-bold text-ink-600 tabular-nums">{stats.planning || 0}</p>
                    </div>
                    <div className="kpi-card text-center animate-fade-up stagger-3">
                        <p className="text-xs font-semibold uppercase tracking-wider text-ink-500">Active</p>
                        <p className="mt-1 text-xl font-bold text-sky-700 tabular-nums">{stats.active || 0}</p>
                    </div>
                    <div className="kpi-card text-center animate-fade-up stagger-4">
                        <p className="text-xs font-semibold uppercase tracking-wider text-ink-500">Review</p>
                        <p className="mt-1 text-xl font-bold text-amber-700 tabular-nums">{stats.review || 0}</p>
                    </div>
                    <div className="kpi-card text-center animate-fade-up stagger-5">
                        <p className="text-xs font-semibold uppercase tracking-wider text-ink-500">Completed</p>
                        <p className="mt-1 text-xl font-bold text-emerald-700 tabular-nums">{stats.completed || 0}</p>
                    </div>
                </div>
            )}

            {projects.length === 0 ? (
                <EmptyState
                    icon={BriefcaseIcon}
                    title="No projects yet"
                    description="Start a new project to track your work."
                    action={
                        <button type="button" onClick={() => setShowCreateForm(true)} className="btn-primary">
                            Create Project
                        </button>
                    }
                />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {projects.map((project) => (
                        <ProjectCard key={project.id} project={project} />
                    ))}
                </div>
            )}

            {/* Create Form Modal */}
            {showCreateForm && (
                <ProjectCreateForm
                    onSuccess={() => {
                        setShowCreateForm(false);
                        loadData();
                    }}
                    onCancel={() => setShowCreateForm(false)}
                />
            )}
        </div>
    );
};

const ProjectCard = ({ project }) => {
    const progress = project.completion_percentage || 0;
    const memberCount = project.members?.length || 0;
    const milestoneCount = project.milestones?.length || 0;
    const completedMilestones = project.milestones?.filter(m => m.status === 'completed').length || 0;

    const statusColors = {
        planning: 'bg-ink-100 text-ink-800',
        active: 'bg-blue-100 text-blue-800',
        review: 'bg-yellow-100 text-yellow-800',
        completed: 'bg-green-100 text-green-800',
        cancelled: 'bg-red-100 text-red-800'
    };

    return (
        <Link to={`/projects/${project.id}`} className="block">
            <div className="card p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <h3 className="font-medium text-ink-950">{project.name}</h3>
                        <p className="text-sm text-ink-600 mt-1 line-clamp-2">
                            {project.description}
                        </p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full capitalize font-medium ${statusColors[project.status] || 'bg-ink-100'}`}>
                        {project.status}
                    </span>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-ink-500">
                    {project.business && (
                        <span>🏢 {project.business.name}</span>
                    )}
                    {project.type && (
                        <span className="capitalize">📋 {project.type.replace('_', ' ')}</span>
                    )}
                    {memberCount > 0 && (
                        <span>👥 {memberCount} members</span>
                    )}
                    {milestoneCount > 0 && (
                        <span>📌 {completedMilestones}/{milestoneCount} milestones</span>
                    )}
                </div>

                <div className="mt-3">
                    <div className="flex justify-between text-xs text-ink-500">
                        <span>Progress</span>
                        <span>{progress}%</span>
                    </div>
                    <div className="mt-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                            className="h-full bg-black transition-all duration-500"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </div>

                <div className="mt-3 flex justify-end">
                    <span className="text-sm text-brand-700 font-semibold flex items-center">
                        View Details <ArrowRightIcon className="w-4 h-4 ml-1" />
                    </span>
                </div>
            </div>
        </Link>
    );
};

const ProjectCreateForm = ({ onSuccess, onCancel }) => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        type: 'individual',
        priority: 'medium',
        business_id: '',
        objective: '',
        scope: '',
        notes: '',
        start_date: '',
        end_date: '',
        estimated_hours: ''
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = { ...formData };
            if (!payload.business_id) delete payload.business_id;
            if (!payload.start_date) delete payload.start_date;
            if (!payload.end_date) delete payload.end_date;
            if (!payload.estimated_hours) delete payload.estimated_hours;
            if (!payload.objective) delete payload.objective;
            if (!payload.scope) delete payload.scope;
            if (!payload.description) delete payload.description;

            await axios.post('/projects', payload);
            toast.success('Project created successfully');
            onSuccess();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to create project');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-border p-4 flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-ink-950">Create New Project</h2>
                    <button onClick={onCancel} className="text-ink-400 hover:text-ink-600">
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-ink-700">
                            Project Name *
                        </label>
                        <input
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            className="input-field"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-ink-700">
                            Description
                        </label>
                        <textarea
                            rows={3}
                            value={formData.description}
                            onChange={(e) => setFormData({...formData, description: e.target.value})}
                            className="input-field"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-ink-700">
                                Type
                            </label>
                            <select
                                value={formData.type}
                                onChange={(e) => setFormData({...formData, type: e.target.value})}
                                className="input-field"
                            >
                                <option value="individual">Individual</option>
                                <option value="team">Team</option>
                                <option value="business">Business</option>
                                <option value="process_mapping">Process Mapping</option>
                                <option value="erp">ERP</option>
                                <option value="ai">AI</option>
                                <option value="pilot">Pilot</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-ink-700">
                                Priority
                            </label>
                            <select
                                value={formData.priority}
                                onChange={(e) => setFormData({...formData, priority: e.target.value})}
                                className="input-field"
                            >
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                                <option value="critical">Critical</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-ink-700">
                                Start Date
                            </label>
                            <input
                                type="date"
                                value={formData.start_date}
                                onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                                className="input-field"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-ink-700">
                                End Date
                            </label>
                            <input
                                type="date"
                                value={formData.end_date}
                                onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                                className="input-field"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-ink-700">
                            Estimated Hours
                        </label>
                        <input
                            type="number"
                            step="0.5"
                            value={formData.estimated_hours}
                            onChange={(e) => setFormData({...formData, estimated_hours: e.target.value})}
                            className="input-field"
                            placeholder="e.g., 40"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-ink-700">
                            Objective
                        </label>
                        <textarea
                            rows={2}
                            value={formData.objective}
                            onChange={(e) => setFormData({...formData, objective: e.target.value})}
                            className="input-field"
                            placeholder="What is the main objective of this project?"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-ink-700">
                            Scope
                        </label>
                        <textarea
                            rows={2}
                            value={formData.scope}
                            onChange={(e) => setFormData({...formData, scope: e.target.value})}
                            className="input-field"
                            placeholder="Define the scope of this project"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-ink-700">
                            Notes
                        </label>
                        <textarea
                            rows={2}
                            value={formData.notes}
                            onChange={(e) => setFormData({...formData, notes: e.target.value})}
                            className="input-field"
                            placeholder="Add any additional notes for this project..."
                        />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t border-border">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-4 py-2 text-ink-700 hover:bg-ink-50 rounded-md transition-colors text-sm font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading || !formData.name}
                            className="btn-primary"
                        >
                            {loading ? 'Creating...' : 'Create Project'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Projects;
